import React, { useState, useContext, useEffect } from "react";
import { Box, Button, Flex, Text, useToast, Heading, VStack, Divider, Spinner, Center, Select } from "@chakra-ui/react";
import axios from "axios";
import { MdOutlineFileDownload } from "react-icons/md";
import { RiArrowDropDownLine } from "react-icons/ri";
import { ApplicationContext } from "../utils/contextsProviders";
import { saveAs } from "file-saver";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// Helper function to extract location display name (index 2 after split by '-')
const extractLocationDisplayName = (locationName) => {
  if (!locationName || typeof locationName !== 'string') {
    return locationName;
  }
  const parts = locationName.split('-');
  if (parts.length >= 3) {
    return parts[2].trim();
  }
  return locationName;
};

// Helper function to extract location name for geocoding API
const extractLocationNameForGeocoding = (locationDisplayName) => {
  if (!locationDisplayName || typeof locationDisplayName !== 'string') {
    return locationDisplayName;
  }
  
  let cleanedName = locationDisplayName;
  
  // Remove common state codes (Australia) - match whole word only
  const stateCodes = ['NSW', 'QLD', 'VIC', 'SA', 'TAS', 'WA', 'NT', 'ACT'];
  stateCodes.forEach(code => {
    cleanedName = cleanedName.replace(new RegExp(`\\b${code}\\b`, 'gi'), '').trim();
  });
  
  // Remove common Belgian region codes
  const belgianRegions = ['BCR', 'FR', 'WR'];
  belgianRegions.forEach(region => {
    cleanedName = cleanedName.replace(new RegExp(`\\b${region}\\b`, 'gi'), '').trim();
  });
  
  // Remove parts after dashes that look like descriptors
  const parts = cleanedName.split(/\s*-\s*/);
  if (parts.length > 1) {
    cleanedName = parts[0].trim();
    if (cleanedName.length < 5 && parts.length > 1) {
      cleanedName = parts.slice(0, 2).join(' ').trim();
    }
  }
  
  // Clean up extra spaces
  cleanedName = cleanedName.replace(/\s+/g, ' ').trim();
  
  return cleanedName || locationDisplayName;
};

// Helper function to determine country from location name
const getCountryFromLocation = (locationName) => {
  if (!locationName || typeof locationName !== 'string') {
    return 'Australia';
  }
  
  const upperName = locationName.toUpperCase();
  
  // Check for Australian state codes
  const australianStates = ['NSW', 'QLD', 'VIC', 'SA', 'TAS', 'WA', 'NT', 'ACT'];
  if (australianStates.some(state => upperName.includes(state))) {
    return 'Australia';
  }
  
  // Check for Belgian regions
  const belgianRegions = ['BRUSSELS', 'FLANDERS', 'WALLONIA', 'BCR'];
  if (belgianRegions.some(region => upperName.includes(region))) {
    return 'Belgium';
  }
  
  // Default to Australia
  return 'Australia';
};

// Helper function to format date as YYYY-MM-DD
const formatDateForAPI = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to calculate date range based on time period
const calculateDateRange = (timePeriod) => {
  const today = new Date();
  let startDate, endDate;
  
  switch (timePeriod) {
    case "day":
      const yesterday = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 1
      );
      startDate = new Date(yesterday);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(yesterday);
      endDate.setHours(23, 59, 59, 999);
      break;
    
    case "week":
      const now = new Date();
      const lastMonday = new Date(now);
      lastMonday.setDate(now.getDate() - ((now.getDay() + 6) % 7) - 7);
      lastMonday.setHours(0, 0, 0, 0);
      const lastSunday = new Date(lastMonday);
      lastSunday.setDate(lastMonday.getDate() + 6);
      lastSunday.setHours(23, 59, 59, 999);
      startDate = lastMonday;
      endDate = lastSunday;
      break;
    
    case "month":
      const lastMonth = new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        1
      );
      startDate = lastMonth;
      endDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        0,
        23,
        59,
        59,
        999
      );
      break;
    
    case "quarter":
      const currentQuarter = Math.floor((today.getMonth() + 3) / 3);
      const lastQuarterStartMonth = currentQuarter * 3 - 6;
      const lastQuarterStart = new Date(
        today.getFullYear(),
        lastQuarterStartMonth,
        1
      );
      startDate = lastQuarterStart;
      endDate = new Date(
        today.getFullYear(),
        lastQuarterStartMonth + 3,
        0,
        23,
        59,
        59,
        999
      );
      break;
    
    case "half-year":
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();
      
      if (currentMonth < 6) {
        startDate = new Date(currentYear - 1, 6, 1);
        endDate = new Date(currentYear - 1, 11, 31, 23, 59, 59, 999);
      } else {
        startDate = new Date(currentYear, 0, 1);
        endDate = new Date(currentYear, 5, 30, 23, 59, 59, 999);
      }
      break;
    
    case "year":
      const lastFiscalYearStart = new Date(
        today.getMonth() >= 6
          ? today.getFullYear() - 1
          : today.getFullYear() - 2,
        6,
        1
      );
      startDate = lastFiscalYearStart;
      endDate = new Date(
        lastFiscalYearStart.getFullYear() + 1,
        5,
        30,
        23,
        59,
        59,
        999
      );
      break;
    
    case "2years":
      const twoYearsStart = new Date(
        today.getMonth() >= 6
          ? today.getFullYear() - 2
          : today.getFullYear() - 3,
        6,
        1
      );
      startDate = twoYearsStart;
      endDate = new Date(
        twoYearsStart.getFullYear() + 2,
        5,
        30,
        23,
        59,
        59,
        999
      );
      break;
    
    default:
      startDate = today;
      endDate = today;
  }
  
  return { startDate, endDate };
};

const BASE_TEMP = 18;

async function getDegreeDaysGrouped({
  latitude,
  longitude,
  startDate,
  endDate
}) {
  const url = `https://archive-api.open-meteo.com/v1/archive?latitude=${latitude}&longitude=${longitude}&start_date=${startDate}&end_date=${endDate}&daily=temperature_2m_max,temperature_2m_min&timezone=auto`;

  const res = await fetch(url);
  const data = await res.json();

  const result = {
    CDD: [],
    HDD: [],
    Neutral: []
  };

  data.daily.time.forEach((date, i) => {
    const maxTemp = data.daily.temperature_2m_max[i];
    const minTemp = data.daily.temperature_2m_min[i];
    const avgTemp = +((maxTemp + minTemp) / 2).toFixed(2);

    if (avgTemp > BASE_TEMP) {
      result.CDD.push({
        date,
        avgTemperature: avgTemp,
        value: +(avgTemp - BASE_TEMP).toFixed(2)
      });
    } else if (avgTemp < BASE_TEMP) {
      result.HDD.push({
        date,
        avgTemperature: avgTemp,
        value: +(BASE_TEMP - avgTemp).toFixed(2)
      });
    } else {
      result.Neutral.push({
        date,
        avgTemperature: avgTemp,
        value: 0
      });
    }
  });

  return result;
}

const PIAMVTab = () => {
  const toast = useToast();
  const { user } = useContext(ApplicationContext);

  // State for company/location structure
  const [companiesData, setCompaniesData] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // State for time period per location
  const [timePeriods, setTimePeriods] = useState({});
  
  // State for CDD/HDD data per location
  const [degreeDaysData, setDegreeDaysData] = useState({});
  
  // State for loading CDD/HDD data per location
  const [loadingDegreeDays, setLoadingDegreeDays] = useState({});
  
  // State for chart data per location
  const [chartData, setChartData] = useState({});
  
  // State for temperature report - time period per location and downloading state
  const [temperatureTimePeriods, setTemperatureTimePeriods] = useState({});
  const [downloadingTemperature, setDownloadingTemperature] = useState({});

  // Fetch locations based on user role
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // First, fetch locations
        let userLocations = [];
        
        if (user.role === "SuperAdmin") {
          // For SuperAdmin, fetch all locations from Smappee API
          try {
            const locationResponse = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/smappee/service-locations`
            );
            
            userLocations = locationResponse.data.serviceLocations
              .map(loc => loc.name)
              .filter(name => !name.includes("CABIN") && !name.includes("Elvy"));
          } catch (error) {
            console.error('Error fetching locations:', error);
          }
        } else {
          // For CustomerAdmin and CustomerUser, use their assigned locations
          userLocations = user.locations || [];
        }

        setLocations(userLocations);

        // Now fetch company data from asset-register API to get company structure
        const config = {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        };

        let filesResponse;
        if (user.role === "SuperAdmin") {
          filesResponse = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/by-company`,
            config
          );
        } else {
          filesResponse = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/user-files`,
            config
          );
        }

        // Group locations by company - use user.companyName for CustomerAdmin/CustomerUser
        // For SuperAdmin, group by the companyName from files or create separate groups
        const companyMap = {};
        
        if (user.role === "SuperAdmin") {
          // For SuperAdmin, group by companyName from files
          // First, get all unique company names from files
          const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [filesResponse.data];
          const companyNamesFromFiles = new Set();
          
          filesData.forEach(companyData => {
            if (companyData.companyName) {
              companyNamesFromFiles.add(companyData.companyName);
            }
          });
          
          // Then group locations by matching them to files' companies
          filesData.forEach(companyData => {
            const companyName = companyData.companyName;
            // Skip if companyName is empty or 'Unknown'
            if (!companyName || companyName.trim() === '' || companyName === 'Unknown') {
              return;
            }
            
            if (!companyMap[companyName]) {
              companyMap[companyName] = {
                companyName,
                locations: {}
              };
            }
            
            // Add locations from files - group by unique display names
            companyData.locations.forEach(locationData => {
              const displayName = extractLocationDisplayName(locationData.location);
              if (!companyMap[companyName].locations[displayName]) {
                companyMap[companyName].locations[displayName] = {
                  location: locationData.location,
                  locationDisplayName: displayName
                };
              }
            });
          });
        } else {
          // For CustomerAdmin/CustomerUser, use user.companyName
          // Make sure companyName is not empty or undefined
          let companyName = user.companyName;
          if (!companyName || companyName.trim() === '') {
            // Try to get from files response if available
            const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [filesResponse.data];
            if (filesData.length > 0 && filesData[0].companyName) {
              companyName = filesData[0].companyName;
            } else {
              // Skip if we can't determine company name (don't show Unknown)
              companyName = null;
            }
          }
          
          // Only proceed if we have a valid company name
          if (companyName && companyName !== 'Unknown') {
            if (!companyMap[companyName]) {
              companyMap[companyName] = {
                companyName,
                locations: {}
              };
            }
          
            // Get unique location display names from user locations
            const uniqueLocationDisplayNames = new Set();
            const locationMap = {}; // Map display name to first full location string
            
            userLocations.forEach(locationName => {
              const displayName = extractLocationDisplayName(locationName);
              uniqueLocationDisplayNames.add(displayName);
              if (!locationMap[displayName]) {
                locationMap[displayName] = locationName; // Store first occurrence
              }
            });
            
            // Add unique locations to company map
            uniqueLocationDisplayNames.forEach(displayName => {
              const fullLocationName = locationMap[displayName];
              companyMap[companyName].locations[displayName] = {
                location: fullLocationName, // Use first full location for operations
                locationDisplayName: displayName
              };
            });
            
            // Merge with files data to ensure consistency
            const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [filesResponse.data];
            
            filesData.forEach(companyData => {
              if (companyData.companyName === companyName) {
                companyData.locations.forEach(locationData => {
                  const displayName = extractLocationDisplayName(locationData.location);
                  if (companyMap[companyName].locations[displayName]) {
                    // Update location if it exists in files (to use the exact location string from files)
                    companyMap[companyName].locations[displayName].location = locationData.location;
                  } else {
                    // Add location from files if not already in map
                    companyMap[companyName].locations[displayName] = {
                      location: locationData.location,
                      locationDisplayName: displayName
                    };
                  }
                });
              }
            });
          }
        }

        // Convert to array format and filter out Unknown companies
        const result = Object.values(companyMap)
          .filter(company => company.companyName && company.companyName !== 'Unknown')
          .map(company => ({
            companyName: company.companyName,
            locations: Object.values(company.locations)
          }));

        setCompaniesData(result);
        
        // Initialize time periods to "week" for all locations
        const initialTimePeriods = {};
        const initialTemperatureTimePeriods = {};
        result.forEach(company => {
          company.locations.forEach(locationData => {
            const locationKey = locationData.locationDisplayName || locationData.location;
            initialTimePeriods[locationKey] = "week";
            initialTemperatureTimePeriods[locationKey] = "day";
          });
        });
        setTimePeriods(initialTimePeriods);
        setTemperatureTimePeriods(initialTemperatureTimePeriods);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          title: "Failed to load data",
          description: error.response?.data?.message || "An error occurred",
          status: "error",
          duration: 3000
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user.role, user.locations, toast]);

  // Handler for time period change (for CDD/HDD)
  const handleTimePeriodChange = (locationKey, timePeriod) => {
    setTimePeriods({
      ...timePeriods,
      [locationKey]: timePeriod
    });
  };

  // Handler for temperature time period change
  const handleTemperatureTimePeriodChange = (locationKey, timePeriod) => {
    setTemperatureTimePeriods({
      ...temperatureTimePeriods,
      [locationKey]: timePeriod
    });
  };

  // Handler for getting CDD and HDD data
  const handleGetCDDHDD = async (locationData) => {
    const locationKey = locationData.locationDisplayName || locationData.location;
    const timePeriod = timePeriods[locationKey] || "week";
    
    setLoadingDegreeDays({ ...loadingDegreeDays, [locationKey]: true });
    
    try {
      // Calculate date range
      const { startDate, endDate } = calculateDateRange(timePeriod);
      const startDateStr = formatDateForAPI(startDate);
      const endDateStr = formatDateForAPI(endDate);
      
      // Extract location name and country
      const locationDisplayName = locationData.locationDisplayName.replace(/^NRMA\s*/i, "") || locationData.location.replace(/^NRMA\s*/i, "");
      const locationName = extractLocationNameForGeocoding(locationDisplayName);
      const country = getCountryFromLocation(locationDisplayName);
      
      // Step 1: Get coordinates from geocoding API
      const geocodingUrl = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(locationName)}&country=${encodeURIComponent(country)}`;
      const geocodingResponse = await axios.get(geocodingUrl);
      
      if (!geocodingResponse.data.results || geocodingResponse.data.results.length === 0) {
        throw new Error('Location not found in geocoding service');
      }
      
      // Find the result matching the country
      const locationResult = geocodingResponse.data.results.find(
        result => result.country === country
      ) || geocodingResponse.data.results[0];
      
      const { latitude, longitude } = locationResult;
      
      // Step 2: Get degree days data
      const degreeDaysResult = await getDegreeDaysGrouped({
        latitude,
        longitude,
        startDate: startDateStr,
        endDate: endDateStr
      });
      
      // Store the raw data
      setDegreeDaysData({
        ...degreeDaysData,
        [locationKey]: degreeDaysResult
      });
      
      // Prepare chart data - combine all dates and sort them
      const allDates = new Set();
      degreeDaysResult.CDD.forEach(item => allDates.add(item.date));
      degreeDaysResult.HDD.forEach(item => allDates.add(item.date));
      degreeDaysResult.Neutral.forEach(item => allDates.add(item.date));
      
      const sortedDates = Array.from(allDates).sort();
      
      const chartDataArray = sortedDates.map(date => {
        const cddItem = degreeDaysResult.CDD.find(item => item.date === date);
        const hddItem = degreeDaysResult.HDD.find(item => item.date === date);
        const neutralItem = degreeDaysResult.Neutral.find(item => item.date === date);
        
        return {
          date,
          CDD: cddItem ? cddItem.value : 0,
          HDD: hddItem ? hddItem.value : 0,
          avgTemperature: cddItem ? cddItem.avgTemperature : (hddItem ? hddItem.avgTemperature : (neutralItem ? neutralItem.avgTemperature : 0))
        };
      });
      
      setChartData({
        ...chartData,
        [locationKey]: chartDataArray
      });
      
      toast({
        title: "CDD and HDD data loaded",
        status: "success",
        duration: 2000
      });
    } catch (error) {
      console.error('Error fetching CDD/HDD data:', error);
      toast({
        title: "Failed to get CDD and HDD data",
        description: error.response?.data?.message || error.message || "An error occurred",
        status: "error",
        duration: 3000
      });
    } finally {
      setLoadingDegreeDays({ ...loadingDegreeDays, [locationKey]: false });
    }
  };

  // Handler for temperature report download
  const handleTemperatureDownload = async (locationData) => {
    const locationKey = locationData.locationDisplayName || locationData.location;
    const downloadKey = `temp-${locationKey}`;
    
    setDownloadingTemperature({ ...downloadingTemperature, [downloadKey]: true });
    
    try {
      // Get time period for this location (default to "day")
      const timePeriod = temperatureTimePeriods[locationKey] || "day";
      
      // Calculate date range
      const { startDate, endDate } = calculateDateRange(timePeriod);
      const startDateStr = formatDateForAPI(startDate);
      const endDateStr = formatDateForAPI(endDate);
      
      // Extract location name and country
      const locationDisplayName = locationData.locationDisplayName.replace(/^NRMA\s*/i, "") || locationData.location.replace(/^NRMA\s*/i, "");
      const locationName = extractLocationNameForGeocoding(locationDisplayName);
      const country = getCountryFromLocation(locationDisplayName);
      
      // Step 1: Get coordinates from geocoding API
      const geocodingUrl = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(locationName)}&country=${encodeURIComponent(country)}`;
      const geocodingResponse = await axios.get(geocodingUrl);
      
      if (!geocodingResponse.data.results || geocodingResponse.data.results.length === 0) {
        throw new Error('Location not found in geocoding service');
      }
      
      // Find the result matching the country
      const locationResult = geocodingResponse.data.results.find(
        result => result.country === country
      ) || geocodingResponse.data.results[0]; // Fallback to first result
      
      const { latitude, longitude } = locationResult;
      
      // Step 2: Get temperature data from archive API
      const archiveUrl = `https://archive-api.open-meteo.com/v1/archive?latitude=${latitude}&longitude=${longitude}&start_date=${startDateStr}&end_date=${endDateStr}&hourly=temperature_2m`;
      const archiveResponse = await axios.get(archiveUrl);
      
      if (!archiveResponse.data.hourly || !archiveResponse.data.hourly.time || !archiveResponse.data.hourly.temperature_2m) {
        throw new Error('Temperature data not available for the selected period');
      }
      
      const { time, temperature_2m } = archiveResponse.data.hourly;
      
      // Step 3: Create CSV
      if (!time || !Array.isArray(time) || time.length === 0) {
        throw new Error('No time data available');
      }
      
      const csvRows = [
        ['Date/Time', 'Temperature (°C)'], // Header
        ...time.map((timestamp, index) => {
          const temp = temperature_2m && Array.isArray(temperature_2m) && index < temperature_2m.length
            ? temperature_2m[index]
            : null;
          return [
            timestamp,
            temp !== null && temp !== undefined && !isNaN(temp)
              ? temp.toFixed(2)
              : 'N/A'
          ];
        })
      ];
      
      // Convert to CSV string
      const csvContent = csvRows.map(row => 
        row.map(cell => {
          // Escape quotes in cell content and wrap in quotes
          const cellStr = String(cell).replace(/"/g, '""');
          return `"${cellStr}"`;
        }).join(',')
      ).join('\n');
      
      // Step 4: Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const fileName = `Temperature_Report_${locationName.replace(/\s+/g, '_')}_${startDateStr}_${endDateStr}.csv`;
      saveAs(blob, fileName);
      
      toast({
        title: "Temperature report downloaded",
        status: "success",
        duration: 2000
      });
    } catch (error) {
      console.error('Temperature download error:', error);
      toast({
        title: "Failed to download temperature report",
        description: error.response?.data?.message || error.message || "An error occurred",
        status: "error",
        duration: 3000
      });
    } finally {
      setDownloadingTemperature({ ...downloadingTemperature, [downloadKey]: false });
    }
  };

  // Handler for downloading CDD and HDD data as CSV
  const handleDownloadCDDHDD = (locationData) => {
    const locationKey = locationData.locationDisplayName || locationData.location;
    const data = degreeDaysData[locationKey];
    
    if (!data) {
      toast({
        title: "No data available",
        description: "Please get CDD and HDD data first",
        status: "warning",
        duration: 3000
      });
      return;
    }
    
    // Create CSV rows
    const csvRows = [
      ['Date', 'Type', 'Average Temperature (°C)', 'Value']
    ];
    
    // Add CDD rows
    data.CDD.forEach(item => {
      csvRows.push([item.date, 'CDD', item.avgTemperature.toFixed(2), item.value.toFixed(2)]);
    });
    
    // Add HDD rows
    data.HDD.forEach(item => {
      csvRows.push([item.date, 'HDD', item.avgTemperature.toFixed(2), item.value.toFixed(2)]);
    });
    
    // Add Neutral rows
    data.Neutral.forEach(item => {
      csvRows.push([item.date, 'Neutral', item.avgTemperature.toFixed(2), item.value.toFixed(2)]);
    });
    
    // Convert to CSV string
    const csvContent = csvRows.map(row => 
      row.map(cell => {
        const cellStr = String(cell).replace(/"/g, '""');
        return `"${cellStr}"`;
      }).join(',')
    ).join('\n');
    
    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = `CDD_HDD_${locationKey.replace(/\s+/g, '_')}_${timePeriods[locationKey] || 'week'}.csv`;
    saveAs(blob, fileName);
    
    toast({
      title: "CSV downloaded",
      status: "success",
      duration: 2000
    });
  };

  return (
    <Box p={{ base: 2, md: 6 }}>
      <Text fontWeight="bold" fontSize={{ base: "md", md: "lg" }} mb={2}>
        PIAM&V
      </Text>

      <Text mb={4} fontSize="sm" color="gray.500">
        Source: Thinque
      </Text>

      {loading ? (
        <Center py={10}>
          <VStack spacing={3}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text color="gray.500">Loading data...</Text>
          </VStack>
        </Center>
      ) : companiesData.length === 0 ? (
        <Text color="gray.500">No locations found.</Text>
      ) : (
        <VStack spacing={6} align="stretch">
          {companiesData.map((company, companyIndex) => (
            <Box key={companyIndex} p={4} borderWidth={1} borderRadius={8} bg="white">
              <Heading size="sm" mb={4}>{company.companyName}</Heading>
              {company.locations.length === 0 ? (
                <Text color="orange.500" fontSize="sm">No locations found for this company.</Text>
              ) : (
                <VStack spacing={4} align="stretch">
                  {company.locations.map((locationData, locIndex) => {
                    const locationKey = locationData.locationDisplayName || locationData.location;
                    const currentChartData = chartData[locationKey] || [];
                    const hasData = currentChartData.length > 0;
                    
                    return (
                      <Box key={locIndex} pl={4} borderLeft="2px solid" borderColor="gray.200">
                        <Text fontWeight="semibold" mb={3} fontSize="sm">
                          {locationData.locationDisplayName || locationData.location}
                        </Text>
                        
                        <Flex gap={2} align="center" mb={4} flexWrap="wrap">
                          <Select
                            value={timePeriods[locationKey] || "week"}
                            variant="filled"
                            onChange={(e) => handleTimePeriodChange(locationKey, e.target.value)}
                            icon={<RiArrowDropDownLine />}
                            fontSize="xs"
                            width="150px"
                            size="sm"
                          >
                            <option value="week">Week</option>
                            <option value="month">Month</option>
                            <option value="quarter">Quarter</option>
                            <option value="half-year">Half Year</option>
                            <option value="year">Year</option>
                            <option value="2years">2 Years</option>
                          </Select>
                          <Button
                            colorScheme="blue"
                            size="sm"
                            onClick={() => handleGetCDDHDD(locationData)}
                            isLoading={loadingDegreeDays[locationKey]}
                            loadingText="Loading"
                            fontSize="xs"
                          >
                            Get CDD and HDD
                          </Button>
                        </Flex>

                        {hasData && (
                          <Box mt={4}>
                            <Flex justifyContent="space-between" alignItems="center" mb={2}>
                              <Text fontSize="sm" fontWeight="semibold">CDD and HDD Chart</Text>
                              <Button
                                leftIcon={<MdOutlineFileDownload />}
                                colorScheme="green"
                                size="sm"
                                onClick={() => handleDownloadCDDHDD(locationData)}
                                fontSize="xs"
                              >
                                Download CDD and HDD Data
                              </Button>
                            </Flex>
                            <Box width="100%" height="400px" mt={4}>
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={currentChartData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis 
                                    dataKey="date" 
                                    angle={-45}
                                    textAnchor="end"
                                    height={80}
                                    interval={Math.floor(currentChartData.length / 10)}
                                  />
                                  <YAxis />
                                  <Tooltip />
                                  <Legend />
                                  <Line 
                                    type="monotone" 
                                    dataKey="CDD" 
                                    stroke="#8884d8" 
                                    name="CDD (Cooling Degree Days)"
                                    strokeWidth={2}
                                  />
                                  <Line 
                                    type="monotone" 
                                    dataKey="HDD" 
                                    stroke="#82ca9d" 
                                    name="HDD (Heating Degree Days)"
                                    strokeWidth={2}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            </Box>
                          </Box>
                        )}

                        <Divider mt={4} />

                        {/* Temperature Report Section */}
                        <Box mt={4}>
                          <Text fontSize="xs" color="gray.600" mb={2}>Temperature Report</Text>
                          <Flex gap={2} align="center" mb={2} flexWrap="wrap">
                            <Select
                              value={temperatureTimePeriods[locationKey] || "day"}
                              variant="filled"
                              onChange={(e) => handleTemperatureTimePeriodChange(locationKey, e.target.value)}
                              icon={<RiArrowDropDownLine />}
                              fontSize="xs"
                              width="150px"
                              size="sm"
                            >
                              <option value="day">Day</option>
                              <option value="week">Week</option>
                              <option value="month">Month</option>
                              <option value="quarter">Quarter</option>
                              <option value="half-year">Half Year</option>
                              <option value="year">Year</option>
                              <option value="2years">2 Years</option>
                            </Select>
                            <Button
                              leftIcon={<MdOutlineFileDownload />}
                              colorScheme="blue"
                              size="sm"
                              onClick={() => handleTemperatureDownload(locationData)}
                              isLoading={downloadingTemperature[`temp-${locationKey}`]}
                              loadingText="Downloading"
                              fontSize="xs"
                            >
                              Download
                            </Button>
                          </Flex>
                        </Box>

                        {locIndex < company.locations.length - 1 && <Divider mt={4} />}
                      </Box>
                    );
                  })}
                </VStack>
              )}
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  );
};

export default PIAMVTab;

